import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import type { IsLoadingResponse } from "swr/_internal";

interface InfiniteScrollHook {
  page?: number;
  setPage?: (page: number | ((prev: number) => number)) => void;
  size?: number;
  setSize?: (size: number | ((prev: number) => number)) => void;
  isLoading?: IsLoadingResponse;
  isValidating?: boolean;
  [key: string]: unknown;
}

/**
 * Wraps a data fetching hook with infinite scroll functionality.
 * Supports both page/setPage and size/setSize patterns.
 *
 * Example:
 * const { data, isLoading, triggerRef } = useInfiniteScroll(useMyHook, { page: 1 });
 *
 * Returns:
 * - triggerRef: React ref to attach to a trigger element (bottom of the list to trigger next page load)
 *   - This should be attached to the bottom of the list like <div ref={triggerRef} />
 * - inView: Boolean indicating if the trigger element is visible
 * - triggerAtPercentage: Percentage from end of PER_PAGE to trigger next page load
 *
 * Page vs Size patterns:
 * - page/setPage: Returns current page data directly.
 *   Example: page 1 → 2 → 3, data: [items 1-10] → [items 11-20] → [items 21-30]
 * - size/setSize: Returns array of all fetched pages.
 *   Example: size 1 → 2 → 3, data: [[page1], [page1,page2], [page1,page2,page3]]
 */
export const useInfiniteScroll = <T extends InfiniteScrollHook, Args>(
  hookCreator: (args: Args) => T,
  hookArgs: Args,
): T & {
  triggerRef: (node?: Element | null) => void;
  inView: boolean;
  triggerAtPercentage: number;
} => {
  const hook = hookCreator(hookArgs);
  const { ref: triggerRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView) {
      if (hook.setPage) {
        hook.setPage((prevPage) => prevPage + 1);
      } else if (hook.setSize) {
        hook.setSize((prevSize) => prevSize + 1);
      } else {
        throw new Error("Hook must have either setPage or setSize for infinite scroll to work.");
      }
    }
  }, [inView, hook.setPage, hook.setSize]);

  return {
    ...hook,
    triggerRef,
    inView,
    triggerAtPercentage: 0.2, // Default to 20% from end
  };
};
